import { useEffect, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// url is like "/api/formation-media/{id}" ; api baseURL already ends with /api
const toApiPath = (url) => (url || "").replace(/^\/api/, "");

function useBlobUrl(mediaUrl, active = true) {
  const [state, setState] = useState({ url: "", loading: true, error: false });
  useEffect(() => {
    let objectUrl = "";
    let cancelled = false;
    if (!mediaUrl || !active) {
      setState({ url: "", loading: false, error: false });
      return () => {};
    }
    setState({ url: "", loading: true, error: false });
    api
      .get(toApiPath(mediaUrl), { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setState({ url: objectUrl, loading: false, error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ url: "", loading: false, error: true });
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaUrl, active]);
  return state;
}

function youtubeId(url) {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : "";
}
function vimeoId(url) {
  const m = (url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : "";
}

export function LessonVideo({ video, poster, testId = "lesson-video" }) {
  const source = video?.source;
  const url = video?.url || "";
  const isUpload = source === "upload";
  const { url: blobUrl, loading, error } = useBlobUrl(url, isUpload);

  if (!video) return null;

  if (source === "link") {
    const yt = youtubeId(url);
    if (yt) {
      return (
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black" data-testid={testId}>
          <iframe
            src={`https://www.youtube.com/embed/${yt}`}
            title="Cours vidéo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        </div>
      );
    }
    const vm = vimeoId(url);
    if (vm) {
      return (
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black" data-testid={testId}>
          <iframe
            src={`https://player.vimeo.com/video/${vm}`}
            title="Cours vidéo"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        </div>
      );
    }
    return (
      <video
        data-testid={testId}
        controls
        controlsList="nodownload"
        poster={poster}
        src={url}
        className="aspect-video w-full rounded-2xl bg-black"
      />
    );
  }

  // uploaded / protected
  if (loading) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-2xl bg-slate-900" data-testid={`${testId}-loading`}>
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-2xl bg-slate-900 text-white/70" data-testid={`${testId}-error`}>
        <Lock className="h-8 w-8" />
        <p className="text-sm">Vidéo protégée indisponible</p>
      </div>
    );
  }
  return (
    <video
      data-testid={testId}
      controls
      controlsList="nodownload"
      poster={poster}
      src={blobUrl}
      className="aspect-video w-full rounded-2xl bg-black"
    />
  );
}

export function ProtectedImage({ mediaUrl, alt = "", className = "" }) {
  const { url, loading } = useBlobUrl(mediaUrl, true);
  if (loading) {
    return <div className={`animate-pulse rounded-xl bg-slate-100 ${className}`} />;
  }
  return <img src={url} alt={alt} className={className} loading="lazy" />;
}

export async function downloadProtectedFile(file) {
  // external link file
  if (!file.file_id && file.url) {
    window.open(file.url, "_blank", "noopener");
    return;
  }
  try {
    const res = await api.get(toApiPath(file.url) + "?download=1", { responseType: "blob" });
    const objectUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = file.name || "fichier";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  } catch (e) {
    toast.error("Téléchargement impossible");
  }
}
